import type { ReactNode } from 'react'
import { AuthoredIcon } from '@/components/admin/shared/authored-icon'
import { hasAuthoredIcon } from '@/lib/icons'

/**
 * The mark for the source a card wears (LG-361 §7) — the one the coach picked
 * where they picked one, else the problem's owning source.
 *
 * Lives here rather than beside the schedule card because every curriculum
 * surface needs it: the schedule card, the drag overlay, the tray card, and
 * next the modal switcher (LG-360 §7) and library picker (LG-362 §2). It takes
 * the two fields it reads instead of a whole card, because those two callers
 * hold an `AdminAssignmentDto` and a source row, not a `TrayCard`.
 *
 * Renders nothing when the source has no icon value at all, for the reason
 * `SourceIconStack` skips iconless sources: in a day column ~130px wide, a
 * placeholder mark costs real title width and says less than its absence. A
 * value that is present but fails to load is a different case — that degrades
 * to `AuthoredIcon`'s muted chip, which keeps the accessible name attached
 * rather than dropping the source silently.
 */
export function SourceMark({
  icon,
  label,
  decorative = false,
}: {
  icon: string | null | undefined
  /** What the source is called. Ignored when `decorative`. */
  label?: string | null
  /**
   * Set where something else already names the source, or where a name would
   * be unreachable: the drag overlay is `aria-hidden` (a focus stop inside
   * hidden content is a trap screen readers cannot explain), and the tray mark
   * sits inside a button whose own `aria-label` wins over subtree content, so
   * the tray puts the source in that label instead.
   */
  decorative?: boolean
}): ReactNode {
  if (!hasAuthoredIcon(icon)) return null
  return (
    // `block`, not the span default: an inline span wrapping a block-level
    // <img> is layout-fragile (anonymous boxes, line-height leakage from
    // whatever type scale the card inherits), so the wrapper states the
    // display mode its content already has.
    <span className="mt-[1px] block shrink-0">
      <AuthoredIcon
        icon={icon}
        size="sm"
        fallbackChip
        // `label` is how this module compensates for marks being decorative by
        // contract (empty `alt` on the image branch, `aria-hidden` on the
        // emoji branch). Without it the mark is unnamed for assistive tech and
        // silent on touch, where no tooltip ever opens.
        {...(decorative || !label ? {} : { label })}
      />
    </span>
  )
}
